import { Box, CircularProgress, Container, Typography } from '@material-ui/core'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import PaymentPopup from '../PayForPublish/PaymentPopup'
import ApplicationsApi from '../../services/applications'
import ShowingsApi from '../../services/showings'
import { Application } from '../../types/applications'
import { Apartment } from '../../types/apartments'
import { isSuperLikeActive } from '../../utils/applications'

type RouteParams = {
  applicationCode: string
}

const ApplicationPayment: FC = () => {
  const { applicationCode } = useParams<RouteParams>()
  const location = useLocation()
  const history = useHistory()
  const { t } = useTranslation()
  const [application, setApplication] = useState<Application | null>(null)
  const [apartment, setApartment] = useState<Apartment | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [productTags, setProductTags] = useState<string[]>(['superLike'])
  const statusParam = new URLSearchParams(location.search).get('status')
  const showStatusError = statusParam === 'error'
  const skipUrl = `/aftershowing/${applicationCode}/steps/1?newApplication=true`
  const isDesktop = useMediaQuery('(min-width:600px)')

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }
    try {
      const stored = window.localStorage.getItem('applicationPaymentProduct')
      if (!stored) {
        return
      }
      const parsed = JSON.parse(stored)
      if (Array.isArray(parsed?.productTags) && parsed.productTags.length) {
        setProductTags(parsed.productTags)
      } else if (typeof parsed === 'string') {
        setProductTags([parsed])
      }
    } catch (err) {
      // ignore JSON issues
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    const fetchApplication = async () => {
      try {
        setLoading(true)
        setError(null)
        const fetchedApplication = await ApplicationsApi.getApplicationByCode(
          applicationCode
        )
        if (!cancelled) {
          setApplication(fetchedApplication)
        }
      } catch (_error) {
        if (!cancelled) {
          setApplication(null)
          setError(t('paymentPopup.applicationPaymentError'))
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }
    fetchApplication()
    return () => {
      cancelled = true
    }
  }, [applicationCode, t])

  useEffect(() => {
    if (application && isSuperLikeActive(application)) {
      history.replace(skipUrl)
    }
  }, [application, history, skipUrl])

  useEffect(() => {
    if (!application) {
      return
    }
    let cancelled = false
    const fetchApartment = async () => {
      try {
        setLoading(true)
        setError(null)
        const data = (await ShowingsApi.getForRentapp(applicationCode)) as {
          apartment?: Apartment
        }
        if (!cancelled) {
          const fetchedApartment = data?.apartment ?? null
          if (!fetchedApartment) {
            setApartment(null)
            setError(t('paymentPopup.applicationPaymentError'))
            return
          }
          setApartment(fetchedApartment)
        }
      } catch (_error) {
        if (!cancelled) {
          setApartment(null)
          setError(t('paymentPopup.applicationPaymentError'))
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }
    fetchApartment()
    return () => {
      cancelled = true
    }
  }, [application, applicationCode, t])

  const isReady = Boolean(application && apartment)

  return (
    <Container maxWidth='sm' style={{ padding: '32px 16px' }}>
      {loading && (
        <Box display='flex' justifyContent='center' marginTop={4}>
          <CircularProgress />
        </Box>
      )}
      {showStatusError && (
        <Typography color='error' align='center'>
          {t('paymentPopup.statusErrorMessage')}
        </Typography>
      )}
      {!loading && error && (
        <Typography color='error' align='center'>
          {error}
        </Typography>
      )}
      {!loading && !error && isReady && (
        <PaymentPopup
          productTags={productTags}
          headertext={t('paymentPopup.headerTexts.superLike')}
          infotext={t('paymentPopup.suggestTexts.superLike')}
          apartment={apartment}
          application={application}
          customerType='application'
          paymentProviderWidth={isDesktop ? '165px' : undefined}
        />
      )}
    </Container>
  )
}

export default ApplicationPayment
